import { useState, useCallback } from 'react';
import { addMonths, addDays, addWeeks, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay } from 'date-fns';

export type CalendarView = 'day' | 'week' | 'month' | 'quarter' | 'semester' | 'year';

export interface DateRange {
  start: Date;
  end: Date;
}

export const useCalendarViews = (initialView: CalendarView = 'month') => {
  const [view, setView] = useState<CalendarView>(initialView);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  const getDateRange = useCallback((date: Date, viewType: CalendarView): DateRange => {
    switch (viewType) {
      case 'day':
        return {
          start: startOfDay(date),
          end: endOfDay(date)
        };
      
      case 'week':
        return {
          start: startOfWeek(date, { weekStartsOn: 0 }), // Sunday
          end: endOfWeek(date, { weekStartsOn: 0 })
        };
      
      case 'month':
        return {
          start: startOfMonth(date),
          end: endOfMonth(date)
        };
      
      case 'quarter': {
        // 3 months view
        const monthStart = startOfMonth(date);
        return {
          start: monthStart,
          end: endOfMonth(addMonths(monthStart, 2))
        };
      }
      
      case 'semester': {
        // 6 months view
        const monthStart = startOfMonth(date);
        return {
          start: monthStart,
          end: endOfMonth(addMonths(monthStart, 5))
        };
      }
      
      case 'year': {
        // 12 months view - from January to December of current year
        const year = date.getFullYear();
        return {
          start: new Date(year, 0, 1), // January 1st
          end: new Date(year, 11, 31, 23, 59, 59) // December 31st
        };
      }
      
      default:
        return {
          start: startOfMonth(date),
          end: endOfMonth(date)
        };
    }
  }, []);

  const currentRange = getDateRange(currentDate, view);

  const navigateNext = useCallback(() => {
    setCurrentDate(prevDate => {
      switch (view) {
        case 'day':
          return addDays(prevDate, 1);
        case 'week':
          return addWeeks(prevDate, 1);
        case 'month':
          return addMonths(prevDate, 1);
        case 'quarter':
          return addMonths(prevDate, 3);
        case 'semester':
          return addMonths(prevDate, 6);
        case 'year':
          return addMonths(prevDate, 12);
        default:
          return addMonths(prevDate, 1);
      }
    });
  }, [view]);

  const navigatePrev = useCallback(() => {
    setCurrentDate(prevDate => {
      switch (view) {
        case 'day':
          return addDays(prevDate, -1);
        case 'week':
          return addWeeks(prevDate, -1);
        case 'month':
          return addMonths(prevDate, -1);
        case 'quarter':
          return addMonths(prevDate, -3);
        case 'semester':
          return addMonths(prevDate, -6);
        case 'year':
          return addMonths(prevDate, -12);
        default:
          return addMonths(prevDate, -1);
      }
    });
  }, [view]);

  const navigateToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const changeView = useCallback((newView: CalendarView, date?: Date) => {
    setView(newView);
    if (date) {
      setCurrentDate(date);
    }
  }, []);

  return {
    view,
    currentDate,
    currentRange,
    changeView,
    navigateNext,
    navigatePrev,
    navigateToday,
    getDateRange
  };
};

